import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { useModule, useModules } from '@/hooks/useModule'
import { useT } from '@/lib/i18n/AdminI18nProvider'

type Props = {
  module: string
  children: ReactNode
  fallback?: ReactNode
}

export function ModuleGate({ module: moduleKey, children, fallback }: Props) {
  const t = useT()
  const { isEnabled, isLoading, source } = useModule(moduleKey)
  const { data: modulesData } = useModules()

  const meta = modulesData?.modules.find((m) => m.key === moduleKey)
  const displayName = meta?.name ?? moduleKey

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        {t('modules.gate.checking')}
      </div>
    )
  }

  if (!isEnabled) {
    if (fallback !== undefined) return <>{fallback}</>

    const sourceHints: Record<string, string> = {
      override: t('modules.gate.sourceHint.override'),
      plan: t('modules.gate.sourceHint.plan'),
      default: t('modules.gate.sourceHint.default'),
    }

    return (
      <div className="mx-auto flex max-w-lg flex-col items-center justify-center rounded-xl border border-dashed border-amber-300/60 bg-amber-50/50 p-10 text-center dark:border-amber-800/50 dark:bg-amber-950/20">
        <div className="mb-3 text-4xl" aria-hidden>
          🔒
        </div>
        <h2 className="text-base font-semibold text-foreground">{t('modules.gate.closed', { name: displayName })}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('modules.gate.noAccess')}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          {sourceHints[source] ?? t('modules.gate.notAvailable')}
        </p>
        <Link
          to="/plan-modules"
          className="mt-4 text-sm font-medium text-mainColor underline underline-offset-2 hover:opacity-80"
        >
          {t('modules.gate.viewAll')}
        </Link>
      </div>
    )
  }

  return <>{children}</>
}
